import { api } from './api';

interface PresignedUrlResponse {
  uploadUrl: string;
  key: string;
  expiresIn: number;
}

interface ImageUrlResponse {
  url: string;
  expiresIn: number;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export const uploadService = {
  /**
   * Upload a profile image for a family member.
   * 1. Gets a presigned PUT URL from the backend
   * 2. Uploads the file directly to S3
   * 3. Returns the S3 key for storage in the database
   */
  async uploadProfileImage(memberId: string, file: File): Promise<string> {
    // Client-side validation
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('File size must be less than 5MB');
    }

    if (!ALLOWED_TYPES.includes(file.type as typeof ALLOWED_TYPES[number])) {
      throw new Error('Please upload a JPEG, PNG, or WebP image');
    }

    // Step 1: Get presigned URL from backend
    const response = await api.post<{ success: boolean; data: PresignedUrlResponse }>(
      '/uploads/presigned-url',
      {
        fileName: file.name,
        contentType: file.type,
        memberId,
        fileSize: file.size,
      }
    );

    const { uploadUrl, key } = response.data;

    // Step 2: Upload directly to S3 via presigned URL
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
        'Content-Length': file.size.toString(),
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed with status ${uploadResponse.status}`);
    }

    // Step 3: Return the S3 key (not the full URL — bucket is private)
    return key;
  },

  /**
   * Get a presigned read URL for a private S3 image.
   * Returns a temporary URL (1 hour expiry) that can be used in <img> tags.
   */
  async getImageUrl(key: string): Promise<string> {
    const response = await api.get<{ success: boolean; data: ImageUrlResponse }>(
      `/uploads/image-url?key=${encodeURIComponent(key)}`
    );

    return response.data.url;
  },
};
